import { useCallback, useEffect, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Form, Input, Modal, Segmented, Switch, Table, Tag, message } from 'antd';
import { post } from '../../api';

const PAGE_SIZE = 20;

export default function Users() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [nickUser, setNickUser] = useState(null);
  const [nickForm] = Form.useForm();

  const fetchUsers = useCallback(async (current) => {
    setLoading(true);
    try {
      const payload = { page: current, size: PAGE_SIZE };
      if (filter !== 'all') {
        payload.isVirtual = filter === 'virtual' ? 1 : 0;
      }
      if (keyword) {
        payload.keyword = keyword;
      }
      const rsp = await post('/admin/user/list', payload);
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setList(rsp.data.list || []);
      setTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, [filter, keyword]);

  useEffect(() => {
    fetchUsers(page);
  }, [page, fetchUsers]);

  const changeFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  const search = (value) => {
    setKeyword(value.trim());
    setPage(1);
  };

  const resetSearch = (event) => {
    if (!event.target.value) {
      setKeyword('');
      setPage(1);
    }
  };

  const toggleDisabled = async (user) => {
    try {
      const rsp = await post('/admin/user/disabled', {
        id: user.id,
        disabled: user.disabled ? 0 : 1,
      });
      if (rsp.code) {
        message.error(rsp.msg || '操作失败');
        return;
      }
      message.success(user.disabled ? '已启用' : '已禁用');
      fetchUsers(page);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    }
  };

  const submitNickname = async (values) => {
    try {
      const rsp = await post('/admin/user/nickname', { id: nickUser.id, ...values });
      if (rsp.code) {
        message.error(rsp.msg || '修改失败');
        return;
      }
      message.success('昵称已修改');
      setNickUser(null);
      fetchUsers(page);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    }
  };

  const columns = [
    { title: '邮箱', dataIndex: 'email', render: (value) => value || '-' },
    {
      title: '昵称',
      dataIndex: 'nickname',
      render: (value, record) => (
        <span className="user-nickname">
          {value || '-'}
          <EditOutlined className="user-edit-icon" onClick={() => setNickUser(record)} />
        </span>
      ),
    },
    {
      title: '类型',
      dataIndex: 'isVirtual',
      render: (value) => (
        <Tag color={value ? 'orange' : 'green'}>{value ? '虚拟' : '正式'}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'disabled',
      render: (value, record) => (
        <Switch
          checked={value === 0}
          checkedChildren="正常"
          unCheckedChildren="禁用"
          onChange={() => toggleDisabled(record)}
        />
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createTime',
      render: (value) => (value ? value.replace('T', ' ').slice(0, 19) : '-'),
    },
  ];

  return (
    <div>
      <div className="list-toolbar">
        <div className="list-title">用户管理</div>
        <div className="list-actions">
          <Segmented
            value={filter}
            onChange={changeFilter}
            options={[
              { label: '全部', value: 'all' },
              { label: '正式', value: 'formal' },
              { label: '虚拟', value: 'virtual' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索邮箱 / 昵称 / 用户名 / 手机号"
            onSearch={search}
            onChange={resetSearch}
            style={{ width: 320 }}
          />
        </div>
      </div>

      <Table
        rowKey="id"
        bordered
        columns={columns}
        dataSource={list}
        loading={loading}
        scroll={{ y: 'calc(100vh - 300px)' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `共 ${count} 条`,
          onChange: setPage,
        }}
      />

      <Modal
        title="修改昵称"
        open={!!nickUser}
        destroyOnClose
        onCancel={() => setNickUser(null)}
        onOk={() => nickForm.submit()}
      >
        <Form form={nickForm} layout="vertical" onFinish={submitNickname} initialValues={{ nickname: nickUser?.nickname }}>
          <Form.Item name="nickname" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}>
            <Input maxLength={50} placeholder="请输入昵称" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
